import { useSearchParams } from "react-router-dom";
import { isEmpty } from "lodash";
import { useCallback, useState, useEffect, useRef } from "react";

export type Params = Record<string, any>;

export function useReturnTo() {
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("return_to");

  let params: Params = {};
  searchParams.forEach((value, key) => {
    params[key] = value;
  });

  const { return_to, ...searchParametrs } = params;
  const searchParametrsString = new URLSearchParams(searchParametrs).toString();

  return returnTo ? `${returnTo}?${searchParametrsString}` : null;
}

export function useParams() {
  let [searchParams, setSearchParams] = useSearchParams();

  // React Router's setSearchParams identity changes with searchParams, and its
  // "functional" updater closes over that same snapshot. Keep refs so callers
  // always mutate the latest params without unstable callback identities.
  const searchParamsRef = useRef(searchParams);
  const setSearchParamsRef = useRef(setSearchParams);
  searchParamsRef.current = searchParams;
  setSearchParamsRef.current = setSearchParams;

  let params: Params = {};
  searchParams.forEach((value, key) => {
    if (value !== "") {
      params[key.replace("[]", "")] = key.includes("[]") ? value?.split(",") : value;
    }
  });

  const addSearchParam = useCallback((key: string, value: string) => {
    const currentParams = new URLSearchParams(searchParamsRef.current);
    const previous = currentParams.toString();

    if (isEmpty(value)) {
      currentParams.delete(key);
    } else {
      currentParams.set(key, value);
    }

    if (currentParams.toString() === previous) return;
    searchParamsRef.current = currentParams;
    setSearchParamsRef.current(currentParams, { replace: true });
  }, []);

  const deleteSearchParams = useCallback((key: string | string[]) => {
    const currentParams = new URLSearchParams(searchParamsRef.current);
    const previous = currentParams.toString();
    const keys = Array.isArray(key) ? key : [key];

    keys.forEach((k) => {
      currentParams.delete(k);
    });

    if (currentParams.toString() === previous) return;
    searchParamsRef.current = currentParams;
    setSearchParamsRef.current(currentParams, { replace: true });
  }, []);

  // Pagination keys stay in the URL for usePagination / Pagination. They are not
  // filter fields — callers spread `params` into GraphQL search input, and an
  // extra `currentPage` key fails validation (empty table after Next).
  const { before, after, currentPage, first, last, ...defaultParams } = params;
  return {
    params: { ...defaultParams },
    addSearchParam,
    deleteSearchParams,
    searchParams,
    searchParamsUrl: searchParams.toString()
  };
}

/**
 * Custom hook for search with URL params and debouncing.
 * Built on top of useParams for automatic URL synchronization.
 *
 * This hook manages both immediate input state and debounced search state,
 * automatically syncing the debounced value to URL params. Perfect for
 * search inputs that need to persist across page refreshes and be shareable.
 *
 * @param {string} paramName - The URL param name to sync with (default: "searchTerm")
 * @param {number} debounceMs - Debounce delay in milliseconds (default: 300)
 * @returns Object with searchValue, debouncedSearch, setSearchValue, clearSearch
 *
 * @example
 * // Basic usage
 * const { searchValue, debouncedSearch, setSearchValue, clearSearch } =
 *   useSearchWithParams();
 *
 * // Use in SearchInput component
 * <SearchInput
 *   query={searchValue}
 *   onChange={setSearchValue}
 *   onClear={clearSearch}
 *   placeholder="Search Students"
 * />
 *
 * // Use debounced value in GraphQL variables
 * useEffect(() => {
 *   setVariables(prev => ({
 *     input: {
 *       ...prev.input,
 *       search: debouncedSearch || undefined
 *     }
 *   }));
 * }, [debouncedSearch]);
 *
 * @example
 * // Custom param name and debounce delay
 * const { searchValue, debouncedSearch, setSearchValue, clearSearch } =
 *   useSearchWithParams({
 *     paramName: "query",
 *     debounceMs: 500
 *   });
 */
export function useSearchWithParams({
  paramName = "searchTerm",
  debounceMs = 300
}: {
  paramName?: string;
  debounceMs?: number;
} = {}) {
  const { params, addSearchParam, deleteSearchParams } = useParams();

  const [searchValue, setSearchValue] = useState(params[paramName] || "");
  const [debouncedSearch, setDebouncedSearch] = useState(params[paramName] || "");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue);

      if (searchValue) {
        addSearchParam(paramName, searchValue);
      } else {
        deleteSearchParams(paramName);
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [searchValue, paramName, debounceMs, addSearchParam, deleteSearchParams]);

  const clearSearch = useCallback(() => {
    setSearchValue("");
    setDebouncedSearch("");
    deleteSearchParams(paramName);
  }, [paramName, deleteSearchParams]);

  return {
    searchValue,
    debouncedSearch,
    setSearchValue,
    clearSearch
  };
}
